import {
  Activity,
  BarChart2,
  Briefcase,
  Building,
  CalendarDays,
  CreditCard,
  Database,
  FlaskConical,
  GraduationCap,
  LayoutDashboard,
  ListChecks,
  Mail,
  MessageSquareText,
  Scale,
  Settings,
  Shield,
  Tags,
  UserSearch,
  Users
} from 'lucide-react';
import AuraIcon from '../components/ui/AuraIcon.jsx';

/**
 * Eine Quelle fuer die gesamte Dashboard-Navigation.
 *
 * Bis September 2026 lagen die drei Rollenlisten als lokale Arrays in
 * `components/DashboardNavigation.js`. Seit die mobile Glasleiste
 * (`dashboards/DashboardBottomNav.js`), das "Mehr"-Blatt und die Desktop-
 * Seitenleiste dieselben Punkte zeigen muessen, waeren das drei Kopien
 * gewesen — also liegen die Daten hier und die Komponenten lesen sie.
 *
 * Aufbau: Gruppen statt einer flachen Liste. Zehn undifferenzierte Eintraege
 * sind eine Suchaufgabe; benannte Abschnitte sind die lokale Navigation, an
 * der man ablesen kann, wo man gerade ist.
 *
 * Felder je Eintrag:
 *   path       Route (absolut)
 *   name       Beschriftung in Seitenleiste und Blatt
 *   shortName  Beschriftung in der Glasleiste (11px, muss auf 375px passen)
 *   icon       lucide-Komponente
 *   index      true = Startseite des Bereichs (fuer die Aktiv-Erkennung, s. u.)
 *   mobile     true = eines der drei Ziele, die dauerhaft in der Glasleiste stehen
 *
 * Ein Icon, eine Bedeutung (Okt 2026), über alle Rollen und die Schüler-App:
 * Sessions sind `CalendarDays` (vorher `MessageCircle`, das in der App „Chats“
 * heißt), Skills `Tags` (vorher `BookOpen`, das ist das Logbuch), Aura immer
 * `AuraIcon` (vorher `Gift` bzw. `Sparkles`), Feedback `MessageSquareText`.
 */

// Bereichsname im Kopf der Seitenleiste. Beantwortet "wo bin ich?" — die alte
// Ueberschrift "Dashboard Navigation" war ein Meta-Label ueber sich selbst.
export const DASHBOARD_AREAS = {
  TEACHER: { label: 'Unterricht', root: '/dashboard/teacher' },
  ADMIN: { label: 'Verwaltung', root: '/dashboard/admin' },
  SUPERADMIN: { label: 'Plattform', root: '/dashboard/super-admin' }
};

const TEACHER_GROUPS = [
  {
    items: [
      { path: '/dashboard/teacher', name: 'Übersicht', shortName: 'Übersicht', icon: LayoutDashboard, index: true, mobile: true }
    ]
  },
  {
    label: 'Meine Klasse',
    items: [
      { path: '/dashboard/teacher/students', name: 'Meine Schüler', shortName: 'Schüler', icon: Users, mobile: true },
      { path: '/dashboard/teacher/sessions', name: 'Sessions', shortName: 'Sessions', icon: CalendarDays, mobile: true }
    ]
  },
  {
    label: 'Betreuung',
    items: [
      { path: '/dashboard/teacher/moderation', name: 'Moderation', shortName: 'Moderation', icon: Shield },
      { path: '/dashboard/teacher/aura', name: 'Aura vergeben', shortName: 'Aura', icon: AuraIcon }
    ]
  },
  {
    label: 'Katalog',
    items: [
      { path: '/dashboard/teacher/skills', name: 'Skills', shortName: 'Skills', icon: Tags }
    ]
  }
];

const ADMIN_GROUPS = [
  {
    items: [
      { path: '/dashboard/admin', name: 'Übersicht', shortName: 'Übersicht', icon: LayoutDashboard, index: true, mobile: true }
    ]
  },
  {
    label: 'Schule',
    items: [
      // `ListChecks` statt `Settings`: Die Einrichtung IST eine Checkliste. Bis
      // September 2026 trugen "Einrichtung" und "Einstellungen" dasselbe Symbol —
      // zwei verschiedene Ziele, ein Piktogramm.
      { path: '/dashboard/admin/setup', name: 'Einrichtung', shortName: 'Setup', icon: ListChecks },
      // Einladungen sind der Hebel, mit dem eine neue Schule ueberhaupt Menschen
      // hereinbekommt. Bis September 2026 lagen sie als Reiter INNERHALB der
      // Nutzerverwaltung, also auf der dritten Ebene und ohne eigene Adresse.
      { path: '/dashboard/admin/invitations', name: 'Einladungen', shortName: 'Einladen', icon: Mail, mobile: true },
      { path: '/dashboard/admin/users', name: 'Nutzer:innen', shortName: 'Nutzer:innen', icon: Users, mobile: true },
      { path: '/dashboard/admin/cohorts', name: 'Kohorten', shortName: 'Kohorten', icon: GraduationCap },
      { path: '/dashboard/admin/skills', name: 'Skills', shortName: 'Skills', icon: Tags }
    ]
  },
  {
    label: 'Betrieb',
    items: [
      { path: '/dashboard/admin/sessions', name: 'Sessions', shortName: 'Sessions', icon: CalendarDays },
      { path: '/dashboard/admin/moderation', name: 'Moderation', shortName: 'Moderation', icon: Shield },
      { path: '/dashboard/admin/aura', name: 'Aura-System', shortName: 'Aura', icon: AuraIcon }
    ]
  },
  {
    label: 'Auswertung',
    items: [
      // Namensliste "Wer hängt wo fest?" (#470), nur ADMIN. Vor den Berichten: Mit ihr kann man handeln.
      { path: '/dashboard/admin/activation', name: 'Aktivierung', shortName: 'Aktivierung', icon: UserSearch },
      { path: '/dashboard/admin/analytics', name: 'Berichte', shortName: 'Berichte', icon: BarChart2 }
    ]
  },
  {
    label: 'System',
    items: [
      { path: '/dashboard/admin/legal', name: 'Rechtliches', shortName: 'Rechtliches', icon: Scale },
      { path: '/dashboard/admin/settings', name: 'Einstellungen', shortName: 'Einstellungen', icon: Settings }
    ]
  }
];

const SUPERADMIN_GROUPS = [
  {
    items: [
      { path: '/dashboard/super-admin', name: 'Übersicht', shortName: 'Übersicht', icon: LayoutDashboard, index: true, mobile: true }
    ]
  },
  {
    label: 'Kunden',
    items: [
      { path: '/dashboard/super-admin/schools', name: 'Schulverwaltung', shortName: 'Schulen', icon: Building, mobile: true },
      { path: '/dashboard/super-admin/crm', name: 'CRM', shortName: 'CRM', icon: Briefcase, mobile: true },
      { path: '/dashboard/super-admin/licenses', name: 'Lizenzen', shortName: 'Lizenzen', icon: CreditCard }
    ]
  },
  {
    label: 'Betrieb',
    items: [
      { path: '/dashboard/super-admin/users', name: 'Nutzer:innen', shortName: 'Nutzer:innen', icon: Users },
      { path: '/dashboard/super-admin/monitoring', name: 'Monitoring', shortName: 'Monitoring', icon: Activity },
      { path: '/dashboard/super-admin/database', name: 'Datenbank', shortName: 'Datenbank', icon: Database }
    ]
  },
  {
    label: 'Rückmeldung',
    items: [
      { path: '/dashboard/super-admin/feedback', name: 'Feedback', shortName: 'Feedback', icon: MessageSquareText },
      { path: '/dashboard/super-admin/research', name: 'Forschung', shortName: 'Forschung', icon: FlaskConical }
    ]
  }
];

const GROUPS_BY_ROLE = {
  TEACHER: TEACHER_GROUPS,
  ADMIN: ADMIN_GROUPS,
  SUPERADMIN: SUPERADMIN_GROUPS
};

/** Alle Gruppen einer Rolle. Unbekannte Rolle (z. B. STUDENT) → leer. */
export const getNavGroups = (role) => GROUPS_BY_ROLE[role] || [];

/** Alle Punkte einer Rolle als flache Liste, in Anzeigereihenfolge. */
export const getNavItems = (role) => getNavGroups(role).flatMap((group) => group.items);

/** Die drei Ziele, die dauerhaft in der mobilen Glasleiste stehen. */
export const getMobileNavItems = (role) => getNavItems(role).filter((item) => item.mobile);

/** Alles, was NICHT in der Glasleiste steht — der Inhalt des "Mehr"-Blatts. */
export const getOverflowNavGroups = (role) => getNavGroups(role)
  .map((group) => ({ ...group, items: group.items.filter((item) => !item.mobile) }))
  .filter((group) => group.items.length > 0);

export const getAreaLabel = (role) => DASHBOARD_AREAS[role]?.label || null;

/**
 * Ist dieser Punkt der aktuelle?
 *
 * Bis September 2026 stand hier ein blosses `pathname.startsWith(item.path)`.
 * Weil der Pfad der Uebersicht (`/dashboard/admin`) das Praefix ALLER
 * Unterseiten ist, war "Uebersicht" auf jeder Seite mit hervorgehoben — es
 * leuchteten immer zwei Punkte gleichzeitig. Der Startpunkt eines Bereichs
 * zaehlt deshalb nur bei exakter Uebereinstimmung.
 */
export const isNavItemActive = (pathname, item) => {
  const path = (pathname || '').replace(/\/+$/, '') || '/';
  if (item.index) return path === item.path;
  return path === item.path || path.startsWith(`${item.path}/`);
};
